'use client';

import { useState } from 'react';
import Link from 'next/link';
import { toast } from 'sonner';
import { Instagram, Linkedin } from 'lucide-react';
import { PageType, PAGE_PATHS, HUSTLANCE_URL, HUSTLANCE_INSTAGRAM_URL, HUSTLANCE_REDDIT_URL, HUSTLANCE_DISCORD_URL, HUSTLANCE_LINKEDIN_URL } from './Router';
import { subscribeToNewsletter } from '../firebase/blog-db';
import { sendEmail } from '../lib/email';
import { Button } from './ui/button';
import { RedditIcon, DiscordIcon } from './icons/BrandIcons';
import { PreferredSourceButton } from './PreferredSourceButton';

interface FooterProps {
  onNavigate?: (page: PageType) => void;
}

export function Footer({}: FooterProps) {
  const [email, setEmail] = useState('');
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const subscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !consent) return;
    setSubmitting(true);
    const trimmed = email.trim();
    try {
      await subscribeToNewsletter(trimmed);
      toast.success("You're subscribed!");
      setEmail('');
      setConsent(false);
      // Best-effort — a failed confirmation email shouldn't undo the subscription itself.
      sendEmail({
        to_email: trimmed,
        subject: "You're subscribed to Hustlance Blog!",
        message: "Thanks for subscribing to Hustlance Blog. You'll get an email whenever we publish a new story.",
      }).catch(() => {});
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const navLinks = [
    { label: 'Home', href: PAGE_PATHS['blog-home'] },
    { label: 'All News', href: PAGE_PATHS['blog-all-news'] },
    { label: 'Search', href: PAGE_PATHS['blog-search'] },
    { label: 'About', href: PAGE_PATHS['blog-about'] },
    { label: 'Contact', href: PAGE_PATHS['blog-contact'] },
  ];

  const socialLinks = [
    { href: HUSTLANCE_INSTAGRAM_URL, label: 'Hustlance on Instagram', icon: Instagram },
    { href: HUSTLANCE_REDDIT_URL, label: 'Hustlance on Reddit', icon: RedditIcon },
    { href: HUSTLANCE_DISCORD_URL, label: 'Hustlance on Discord', icon: DiscordIcon },
    { href: HUSTLANCE_LINKEDIN_URL, label: 'Hustlance on LinkedIn', icon: Linkedin },
  ];

  return (
    <footer className="bg-gray-900 py-16 sm:py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-2 gap-12 md:gap-8">
          <div>
            <h2 className="font-blocky text-2xl sm:text-3xl text-white mb-6">Subscribe to Our Newsletter</h2>
            <form onSubmit={subscribe} className="max-w-md">
              <label htmlFor="footer-email" className="block text-white/70 text-sm mb-2">
                Enter your email here <span aria-hidden="true">*</span>
              </label>
              <input
                id="footer-email"
                type="email"
                required
                value={email}
                onChange={e => setEmail(e.target.value)}
                className="w-full bg-transparent border-b border-white/30 text-white placeholder:text-white/40 py-2 mb-5 outline-none focus:border-white transition-colors"
                placeholder=""
              />
              <div className="flex flex-wrap items-center gap-4">
                <label className="flex items-center gap-2.5 text-white/70 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={consent}
                    onChange={e => setConsent(e.target.checked)}
                    className="w-4 h-4 rounded border-white/30 bg-transparent"
                  />
                  Yes, subscribe me to your newsletter.<span aria-hidden="true"> *</span>
                </label>
                <Button type="submit" disabled={submitting || !consent} className="bg-hustlance-primary hover:opacity-90 text-white">
                  {submitting ? 'Subscribing...' : 'Subscribe'}
                </Button>
              </div>
            </form>
          </div>

          <div className="md:justify-self-end">
            <nav className="space-y-4" aria-label="Footer navigation">
              {navLinks.map(link => (
                <Link key={link.href} href={link.href} className="block font-bold text-lg text-white hover:text-white/70 transition-colors">
                  {link.label}
                </Link>
              ))}
              <a href={HUSTLANCE_URL} className="block font-bold text-lg text-white hover:text-white/70 transition-colors">
                Hustlance
              </a>
              <a href={`${HUSTLANCE_URL}/privacy`} className="block text-sm text-white/70 hover:text-white transition-colors pt-2">
                Privacy Policy
              </a>
              <a href={`${HUSTLANCE_URL}/terms`} className="block text-sm text-white/70 hover:text-white transition-colors">
                Terms of Service
              </a>
            </nav>
          </div>
        </div>

        <div className="border-t border-white/10 mt-14 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-white/70 text-sm">© 2026 Hustlance. All rights reserved.</p>
          <div className="flex items-center gap-4">
            {socialLinks.map(({ href, label, icon: Icon }) => (
              <a key={label} href={href} target="_blank" rel="noopener noreferrer" aria-label={label} className="text-white/70 hover:text-white transition-colors">
                <Icon className="w-5 h-5" />
              </a>
            ))}
          </div>
        </div>
        <div className="mt-4 flex justify-center">
          <PreferredSourceButton theme="dark" />
        </div>
      </div>
    </footer>
  );
}
